import React from 'react';
import { ProcessingResultsSkeleton } from '@/components/ui/Skeleton';

interface StatusMessageProps {
  status?: string;
}

/**
 * Displays an error status message when processing has failed.
 */
export const ProcessingErrorMessage: React.FC = () => (
  <div className="bg-red-50 p-4 rounded-md">
    <div className="flex">
      <div className="flex-shrink-0">
        <svg 
          className="h-5 w-5 text-red-400" 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 20 20" 
          fill="currentColor"
          aria-hidden="true"
        >
          <path 
            fillRule="evenodd" 
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" 
            clipRule="evenodd" 
          />
        </svg>
      </div>
      <div className="ml-3">
        <h3 className="text-sm font-medium text-red-800">Processing failed</h3>
        <div className="mt-2 text-sm text-red-700">
          <p>There was an error processing this document. Please try again.</p>
        </div>
      </div>
    </div>
  </div>
);

/**
 * Displays an in-progress status message when processing is ongoing.
 */
export const ProcessingInProgressMessage: React.FC<StatusMessageProps> = ({ status }) => (
  <div className="bg-blue-50 p-4 rounded-md">
    <div className="flex">
      <div className="flex-shrink-0">
        <svg 
          className="animate-spin h-5 w-5 text-blue-400" 
          xmlns="http://www.w3.org/2000/svg" 
          fill="none" 
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path 
            className="opacity-75" 
            fill="currentColor" 
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" 
          />
        </svg>
      </div>
      <div className="ml-3">
        <h3 className="text-sm font-medium text-blue-800">Processing in progress</h3>
        <div className="mt-2 text-sm text-blue-700">
          <p>Your document is currently being processed ({status}). This may take a few moments.</p>
        </div>
      </div>
    </div>
  </div>
);

/**
 * Displays a message when the document has not been processed yet.
 */
export const NotProcessedMessage: React.FC = () => (
  <div className="bg-gray-50 p-4 rounded-md">
    <div className="flex">
      <div className="flex-shrink-0">
        <svg 
          className="h-5 w-5 text-gray-400" 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 20 20" 
          fill="currentColor"
          aria-hidden="true"
        >
          <path 
            fillRule="evenodd" 
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" 
            clipRule="evenodd" 
          />
        </svg>
      </div>
      <div className="ml-3">
        <h3 className="text-sm font-medium text-gray-800">Not processed</h3>
        <div className="mt-2 text-sm text-gray-700">
          <p>This document has not been processed yet. Use the Process button to analyze it.</p>
        </div>
      </div>
    </div>
  </div>
);

/**
 * Loading spinner component for when results are being fetched.
 * Shows a skeleton preview of the expected content structure.
 */
export const LoadingSpinner: React.FC = () => (
  <ProcessingResultsSkeleton />
);
